import React, { useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { EditDisposition } from "./modals/EditDisposition";
import { Edit, MapPin } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { Badge } from "@/components/ui/badge";

const GET_DISPOSITION_DATA = gql`
  query GetPlanData($odrNumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrNumber) {
      location{
      custodyOrCommunity
      pharmacyNotes
      location
      locationDetail
      contact
      dischargePlan
      highPriorityStatus
      ihAssignmentDate
      ihRequestDate
      ihRequestStatus
      notes
      pshFundingSource
      pshSearchNotes
      pshSearchStatus
      pshSearchStatusDate
      pshType
      comments
      restrictions}
    }
  }
`;

const InfoBlock = ({ label, value }: { label: string; value?: string | React.ReactNode }) => {
  const display = value && value !== "" ? value : "—";
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 whitespace-pre-wrap break-words">{display}</span>
    </div>
  );
};

export const PlanAndLocation = ({
  odrNumber,
  programNumber,
}: {
  odrNumber: number;
  programNumber: number;
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { loading, error, data } = useQuery(GET_DISPOSITION_DATA, {
    variables: { odrNumber },
    skip: !odrNumber,
  });

  if (loading) return <TabSpinner label="Loading plan data..." />;
  if (error) return <div className="flex items-center justify-center h-32"><p className="text-xs text-red-500">Error loading plan data</p></div>;
  if (!data) return null;

  const planData = data.getParticipantByODRNumber.location; 

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };

  if (!planData) return <div className="flex items-center justify-center h-32"><p className="text-xs text-gray-500">No plan data found</p></div>;

  return (
    <div className="space-y-4">
      <TabHeader
        icon={MapPin}
        title="Plan and Location"
        subtitle="Custody status, housing, and notes"
        action={
          <button 
            onClick={handleEditClick}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        }
      />
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100">
          {planData.custodyorcommunity == 1 ? "In Custody" : "Community"}
        </Badge>
        {planData.high_priority_status && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-100">
            {planData.high_priority_status}
          </Badge>
        )}
      </div>

      <Card className="rounded-none border-0 shadow-none">
        <CardContent className="p-0">
          <div className="border-t border-b border-gray-200 p-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock label="Location" value={planData.location} />
              <InfoBlock label="Location Detail" value={planData.locationdetail} />
              <InfoBlock label="Contact" value={planData.contact} />
              <InfoBlock label="Discharge Plan" value={planData.dischargeplan} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock label="IH Assignment Date" value={planData.ih_assignment_date ? new Date(planData.ih_assignment_date).toLocaleDateString() : "—"} />
              <InfoBlock label="IH Request Date" value={planData.ih_request_date ? new Date(planData.ih_request_date).toLocaleDateString() : "—"} />
              <InfoBlock label="IH Request Status" value={planData.ih_request_status} />
              <InfoBlock label="Restrictions" value={planData.restrictions} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock label="PSH Funding Source" value={planData.psh_funding_source} />
              <InfoBlock label="PSH Type" value={planData.psh_type} />
              <InfoBlock label="PSH Search Status" value={planData.psh_search_status} />
              <InfoBlock label="PSH Search Status Date" value={planData.psh_search_status_date ? new Date(planData.psh_search_status_date).toLocaleDateString() : "—"} />
              <InfoBlock label="PSH Search Notes" value={planData.psh_search_notes} />
              <InfoBlock label="Pharmacy Notes" value={planData.pharmacynotes} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock label="Notes" value={planData.notes} />
              <InfoBlock label="Comments" value={planData.comments} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <EditDisposition
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        programNumber={programNumber}
        planData={planData}
      />
    </div>
  );
};

export default PlanAndLocation;
